import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { logger } from '@/utils/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    logger.error('ErrorBoundary caught error', error);
    return { 
      hasError: true, 
      error
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Error details:', { error, errorInfo });
    logger.error('Error stack:', error.stack);
    console.error('Application error:', error);
    // Re‑throw error after logging to surface it to global handlers
    setTimeout(() => { throw error; });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  private handleReload = () => {
    // Clear service worker cache before reload for fresh start
    const clearCacheAndReload = async () => {
      try {
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map(name => caches.delete(name)));
        }
      } catch (error) {
        console.error('Cache clear error:', error);
      } finally {
        window.location.reload();
      }
    };
    
    clearCacheAndReload();
  };

  private isDynamicImportError = (error: Error): boolean => {
    return error.message.includes('Failed to fetch dynamically imported module') ||
           error.message.includes('Failed to load') ||
           error.message.includes('Loading chunk');
  };

  public render() {
    if (this.state.hasError) {
      const isDynamicError = this.state.error && this.isDynamicImportError(this.state.error);
      
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
          <div className="text-6xl mb-6">😔</div>
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h1 className="text-2xl font-bold mb-3 text-center">
            {isDynamicError ? 'Update Available' : 'Something went wrong'}
          </h1>
          <p className="text-muted-foreground text-center max-w-md mb-8">
            {isDynamicError 
              ? 'A new version of the app is available. Please reload the page to get the latest update.'
              : "We're sorry, but something unexpected happened. Please try refreshing the page or going back to the home screen."
            }
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={this.handleReload}>
              {isDynamicError ? 'Reload App' : 'Refresh Page'}
            </Button>
            {!isDynamicError && (
              <Button onClick={this.handleReset}>
                Go to Home
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
